import React, { useState } from 'react';

import { DialogActions, Typography } from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { gridFilterModelSelector } from '@mui/x-data-grid-premium';
import {
  AlineButton,
  AlineDialog,
  ConfirmationDialog,
  createUpdateAllDocsMsg,
  UpdateDocumentsAuthorizationAlert,
} from 'src/components';
import { DocumentStatusSelect } from 'src/components/reports/report-detail/GridStatusCell';
import { Apollo } from 'src/gql/generated';
import { getSelectedGroup, useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { writeDocumentStatusJobToCache } from '../reports/report-detail/document-status-job-cache';
import {
  formatSetDocumentsStatusMessage,
  patchSetDocumentsStatusCache,
} from '../reports/report-detail/grid-document-status';

const useStyles = makeStyles()(theme => ({
  description: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: '20px',
  },
  paper: {
    width: 566,
  },
  statusField: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  statusLabel: {
    ...theme.appTypography.fieldLabel,
  },
}));

interface SetDocumentsStatusDialogProps {
  apiRef: React.RefObject<GridApiPremium | null>;
  onClose: () => void;
  open: boolean;
  selectedDocumentIds: UUID[];
  totalDocumentCount: number;
  reportId?: UUID;
  shouldRunForAllDocuments?: boolean;
}

export function SetDocumentsStatusDialog({
  apiRef,
  onClose,
  open,
  reportId,
  selectedDocumentIds,
  shouldRunForAllDocuments = false,
  totalDocumentCount,
}: SetDocumentsStatusDialogProps): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const selectedGroup = getSelectedGroup(apiRef);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [status, setStatus] = useState<Apollo.DocumentStatus | null>(null);
  const [setDocumentsStatus, { loading: submitting }] = Apollo.useSetDocumentsStatusMutation();
  const runAllDocs = Boolean(selectedGroup) || shouldRunForAllDocuments;
  async function handleApply(): Promise<void> {
    if (!apiRef.current || !status) return;

    const filterModel = gridFilterModelSelector(apiRef);
    const { data } = await setDocumentsStatus({
      update(cache, result) {
        const payload = result.data?.setDocumentsStatus;
        if (!payload) return;

        patchSetDocumentsStatusCache(cache, payload);
        if (payload.jobId)
          writeDocumentStatusJobToCache(cache, {
            __typename: 'DocumentStatusJob',
            batches: [],
            id: payload.jobId,
            numQueued: payload.numQueued,
            status: payload.status,
          });
      },
      variables: {
        input: {
          documentIds: runAllDocs ? [] : selectedDocumentIds,
          filterModel,
          groupSelection: selectedGroup?.groupSelection ?? null,
          reportId,
          runAllDocs,
          status,
        },
      },
    });
    const payload = data?.setDocumentsStatus;
    if (payload) {
      notifyStatusResult(payload);
      apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' });
    }
    setConfirmDialogOpen(false);
    setStatus(null);
    onClose();
  }

  // A background job reports through its progress card instead.
  function notifyStatusResult(payload: Apollo.SetDocumentsStatusMutation['setDocumentsStatus']) {
    if (payload.jobId) return;

    dispatch({
      payload: {
        autoHideDuration: 15000,
        key: uuid4(),
        message: formatSetDocumentsStatusMessage(payload),
        variant: payload.numSuccess > 0 ? 'success' : 'info',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  return (
    <AlineDialog
      classes={{ paper: classes.paper }}
      open={open}
      title="Set status"
      onClose={onClose}
    >
      <UpdateDocumentsAuthorizationAlert />
      <Typography className={classes.description}>
        {getSetStatusDescription({
          groupDocumentCount: selectedGroup?.docCount ?? null,
          selectAllMatchingFilters: shouldRunForAllDocuments,
          selectedCount: selectedDocumentIds.length,
        })}
      </Typography>
      <div className={classes.statusField}>
        <label className={classes.statusLabel} htmlFor="set-documents-status">
          New Status:
        </label>
        <DocumentStatusSelect id="set-documents-status" value={status} onChange={setStatus} />
      </div>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!status || confirmDialogOpen}
          loading={submitting && !confirmDialogOpen}
          variant="contained"
          onClick={() => {
            if (runAllDocs) {
              setConfirmDialogOpen(true);
              return;
            }
            void handleApply();
          }}
        >
          Set Status
        </AlineButton>
      </DialogActions>
      <ConfirmationDialog
        message={createUpdateAllDocsMsg(
          `set the status to ${status ?? 'the selected status'}`,
          selectedGroup?.docCount ?? totalDocumentCount
        )}
        open={confirmDialogOpen}
        primaryText="Yes"
        secondaryText="No"
        title="Set status for all matching documents?"
        onClickPrimary={() => {
          void handleApply();
        }}
        onClose={() => setConfirmDialogOpen(false)}
      />
    </AlineDialog>
  );
}

function getSetStatusDescription({
  groupDocumentCount,
  selectAllMatchingFilters,
  selectedCount,
}: {
  groupDocumentCount: number | null;
  selectAllMatchingFilters: boolean;
  selectedCount: number;
}): string {
  if (groupDocumentCount !== null) {
    const noun = groupDocumentCount === 1 ? 'document' : 'documents';
    return `Update the status of all ${groupDocumentCount} ${noun} in this group.`;
  }

  if (selectAllMatchingFilters)
    return 'Update the status of all documents that match the current filters.';

  const noun = selectedCount === 1 ? 'document' : 'documents';
  return `Update the status of ${selectedCount} selected ${noun}.`;
}
